import { getDateIndex, TASK_HEIGHT, TASK_SPACING, CATEGORY_PADDING } from './ganttUtils';

/**
 * Calculate task bar styles for rendering
 * @param {Object} task - The task object
 * @param {Array} visibleDayIndices - Array of visible day indices
 * @param {Number} dayWidth - Width of each day column
 * @param {Date} baseDate - Base date for calculations
 * @param {Number} level - Vertical level/row for the task bar
 * @param {String} taskColor - Background color for the task bar
 * @param {String} textColor - Text color for the task bar
 * @param {Boolean} isGhost - Whether this is a ghost task
 * @param {Object} theme - Material-UI theme object
 * @param {Function} onTaskClick - Click handler function (if any)
 * @param {Boolean} isEditMode - Whether edit mode is active
 * @param {Boolean} isCompleted - Whether task is completed
 * @returns {Array} Array of style objects for task bar segments
 */
export const calculateTaskBarStyles = (
  task,
  visibleDayIndices,
  dayWidth,
  baseDate,
  level,
  taskColor,
  textColor,
  isGhost,
  theme,
  onTaskClick,
  isEditMode,
  isCompleted
) => {
  const taskStartIndex = getDateIndex(task.startDate, baseDate);
  const taskEndIndex = getDateIndex(task.endDate, baseDate);

  // Find the intersection of task duration and visible range
  // visibleDayIndices is always a contiguous range, so use direct math (O(1)) instead of includes/indexOf (O(n))
  const visibleStart = visibleDayIndices[0];
  const visibleEnd = visibleDayIndices[visibleDayIndices.length - 1];
  const intersectionStart = Math.max(taskStartIndex, visibleStart);
  const intersectionEnd = Math.min(taskEndIndex, visibleEnd);

  if (intersectionStart > intersectionEnd) {
    return []; // No intersection - don't render
  }

  // Since visibleDayIndices is contiguous, position = dayIndex - visibleStart (O(1))
  const startPosition = intersectionStart - visibleStart;
  const endPosition = intersectionEnd - visibleStart;
  const width = (endPosition - startPosition + 1) * dayWidth;
  const left = startPosition * dayWidth;
  const top = CATEGORY_PADDING + level * (TASK_HEIGHT + TASK_SPACING);

  return [{
    left: `${left + 2}px`,
    width: `${width - 4}px`,
    top: `${top}px`,
    backgroundColor: `${taskColor}`,
    position: 'absolute',
    height: `${TASK_HEIGHT}px`,
    borderRadius: '24px',
    display: 'flex',
    justifyContent: 'flex-start',
    paddingLeft: '12px',
    paddingRight: '4px',
    color: textColor,
    fontSize: '15px',
    fontWeight: isGhost ? '100' : '500',
    letterSpacing: -0.2,
    zIndex: 15,
    boxShadow: isGhost ? 'none' : theme.shadows[1],
    border: isGhost
      ? `2px dashed`
      : `1px solid`,
    borderColor: isGhost ? theme.palette.grey[50] : taskColor,
    borderOpacity: 0.8,
    overflow: 'hidden',
    cursor: isGhost
      ? 'default'
      : isEditMode
        ? (isCompleted ? 'not-allowed' : 'grab')
        : 'pointer',
    '&:active': {
      cursor: isGhost ? 'default' : isCompleted ? 'not-allowed' : 'grabbing',
    },
    opacity: isGhost ? 1 : 1,
    fontStyle: isGhost ? 'italic' : 'normal',
    '&:hover': onTaskClick ? {
      boxShadow: isGhost ? theme.shadows[1] : theme.shadows[3],
      opacity: isGhost ? 0.95 : 1,
      backgroundColor: isGhost ? theme.palette.grey[50] : undefined,
    } : {},
    // Add a subtle pattern for ghost tasks using gray
    ...(isGhost && {
      backgroundImage: `repeating-linear-gradient(
        45deg,
        #afb6bc,
        #afb6bc 2px,
        #afb6bc 2px,
        #afb6bc 6px
      )`,
    }),
  }];
};
